import React, { useCallback } from "react";
import { connect } from "react-redux";

import clsx from "clsx";
import { bindActionCreators, Dispatch } from "redux";

import { PlusIcon, RedCrossMenuIcon } from "@plex/icons";
import { Dropdown, Form, InputSize, TextInput, ValidationMessage, Link } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens";

import { messageFormatActions } from "../MessageId.actions";
import { TokenFormat } from "../MessageId.helpers";
import styles from "../MessageId.module.scss";
import { IMessageIdState, IToken, ITokenDescriptionValidationResult } from "../MessageId.state";

interface ITokensSectionProps {
  text: string;
  tokens: IToken[];
  descriptionsValidationErrors: ITokenDescriptionValidationResult;
  changeTokenType: (tokenType: { type: string; index: number }) => void;
  changeTokenDescription: (tokenDescription: { index: number; description: string }) => void;
  changeCustomTokenName: (tokenName: { index: number; name: string }) => void;
  changeTextPreview: (previewText: string) => void;
  addCustomToken: () => void;
  removeCustomToken: (id: string) => void;
  setTokensDescriptionValidationState: (validationState: ITokenDescriptionValidationResult) => void;
}

interface ITokenTypeDropdown {
  tokenKey: string;
  type: string;
  index: number;
}

const TokensSection: React.FC<ITokensSectionProps> = ({
  text,
  tokens,
  descriptionsValidationErrors,
  changeTokenType,
  changeTokenDescription,
  changeCustomTokenName,
  changeTextPreview,
  addCustomToken,
  removeCustomToken,
  setTokensDescriptionValidationState
}) => {
  const tokenFormats = [TokenFormat.date, TokenFormat.number, TokenFormat.plural, TokenFormat.select, TokenFormat.selectOrdinal, TokenFormat.time, TokenFormat.variable];

  const localizedValues = useLocalizedValues(() => ({
    tokenDescriptionsHeading: displayMessages.heading("tokenDescriptions"),
    descriptionRequiredMessage: validationMessages.error("tokenMustHaveDescription"),
    tokenMustHaveName: validationMessages.error("tokenMustHaveName"),
    duplicatedTokensInMessage: validationMessages.error("duplicatedTokensInMessage"),
    tokenInvalidTypeMessage: validationMessages.error("tokenInvalidType"),
    addTokenLink: displayMessages.action("addCustomToken"),
    tokenNameHeader: displayMessages.property("tokenName"),
    descriptionHeader: displayMessages.property("description"),
    tokenTypeHeader: displayMessages.property("tokenType")
  }));

  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const isReadOnlyMode = !roles.PlexTechnicalWriter;

  const onTypeChanged = useCallback(
    (type: ITokenTypeDropdown[], token: IToken) => {
      const errors = { ...descriptionsValidationErrors };
      errors[token.id] = {
        ...descriptionsValidationErrors[token.id],
        invalidTokenType: !token.customToken && type[0].type !== token.originalType
      };

      changeTextPreview(errors[token.id].invalidTokenType ? "" : text);

      changeTokenType({ index: type[0].index, type: type[0].type });
      setTokensDescriptionValidationState(errors);
    },
    [text, descriptionsValidationErrors, changeTextPreview, changeTokenType, setTokensDescriptionValidationState]
  );

  const onDescriptionChanged = useCallback(
    (index: number, description: string | null) => {
      changeTokenDescription({ index, description: description ? description : "" });
    },
    [changeTokenDescription]
  );

  const onCustomTokenNameChanged = useCallback(
    (index: number, name: string | null) => {
      changeCustomTokenName({ index, name: name ? name : "" });
    },
    [changeCustomTokenName]
  );

  const onDescriptionBlur = useCallback(
    (token: IToken) => {
      const errors = { ...descriptionsValidationErrors };
      errors[token.id] = {
        ...descriptionsValidationErrors[token.id],
        descriptionRequired: token.description ? false : true
      };

      setTokensDescriptionValidationState(errors);
    },
    [descriptionsValidationErrors, setTokensDescriptionValidationState]
  );

  const onTokenNameBlur = useCallback(
    (token: IToken) => {
      const errors = { ...descriptionsValidationErrors };
      errors[token.id] = {
        ...descriptionsValidationErrors[token.id],
        tokenNameRequired: token.name ? false : true,
        duplicateTokenName: tokens.filter(t => t.name === token.name).length > 1
      };

      setTokensDescriptionValidationState(errors);
    },
    [tokens, descriptionsValidationErrors, setTokensDescriptionValidationState]
  );

  const onDeleteClick = useCallback(
    (id: string) => {
      removeCustomToken(id);
    },
    [removeCustomToken]
  );

  const tokenNameValidationMessage = (id: string): string => {
    if (descriptionsValidationErrors[id]) {
      if (descriptionsValidationErrors[id].tokenNameRequired) {
        return localizedValues.tokenMustHaveName;
      }
      if (descriptionsValidationErrors[id].duplicateTokenName) {
        return localizedValues.duplicatedTokensInMessage;
      }
    }

    return "";
  };

  const renderTokenName = (name: string) => {
    return <div className={styles.tokenDescriptionCell}>{name}</div>;
  };

  const renderTokenNameInput = (token: IToken, index: number) => {
    return (
      <div className={clsx(styles.tokenDescriptionCell, styles.messageFormatInputContainer)}>
        <div className={styles.tokenDescriptionRequired}>
          <TextInput
            id={`token-name-${token.id}`}
            name={token.id}
            className={clsx(tokenNameValidationMessage(token.id) && styles.invalidField, styles.messageFormatInput)}
            size={InputSize.medium}
            value={token.name}
            onValueChange={value => onCustomTokenNameChanged(index, value)}
            onBlur={() => {
              onTokenNameBlur(token);
            }}
          />
        </div>
        <ValidationMessage message={tokenNameValidationMessage(token.id)} />
      </div>
    );
  };

  const renderAddCustomTokenLink = (
    <div className={styles.addTokenLinkContainer}>
      <div className={styles.plusIconContainer}>
        <PlusIcon className={styles.plusIcon} />
      </div>
      <div className={styles.addTokenLink}>
        <Link onClick={addCustomToken}>{localizedValues.addTokenLink}</Link>
      </div>
    </div>
  );

  const renderDescriptionHeader = (
    <div className={styles.tokenDescriptionHeaderRow}>
      <div className={styles.tokenDescriptionCellHeader}>{localizedValues.tokenNameHeader}</div>
      <div className={styles.tokenDescriptionCellHeader}>{localizedValues.tokenTypeHeader}</div>
      <div className={styles.tokenDescriptionCellHeader}>{localizedValues.descriptionHeader}</div>
    </div>
  );

  return (
    <Form.Section title={localizedValues.tokenDescriptionsHeading}>
      <div>
        {tokens && tokens.length > 0 && renderDescriptionHeader}
        {tokens.map((token: IToken, index: number) => {
          const tokenTypes: ITokenTypeDropdown[] = [];
          tokenFormats.forEach(format => {
            tokenTypes.push({
              tokenKey: token.name,
              index,
              type: format
            });
          });

          return (
            <div key={token.id} className={styles.tokenDescriptionRow}>
              {token.customToken ? renderTokenNameInput(token, index) : renderTokenName(token.name)}
              <div className={clsx(styles.tokenDescriptionCell, styles.messageFormatInputContainer)}>
                <Dropdown
                  id={`token-type-${token.id}`}
                  className={clsx(
                    descriptionsValidationErrors[token.id] && descriptionsValidationErrors[token.id].invalidTokenType && styles.invalidField,
                    styles.messageFormatInput
                  )}
                  selected={[{ tokenKey: token.name, index, type: token.type }]}
                  keySelector={row => (row as ITokenTypeDropdown).type}
                  displaySelector={row => (row as ITokenTypeDropdown).type}
                  items={tokenTypes}
                  onSelectionChanged={(newType: ITokenTypeDropdown[]) => onTypeChanged(newType, token)}
                  disabled={isReadOnlyMode}
                />
                <ValidationMessage
                  message={descriptionsValidationErrors[token.id] && descriptionsValidationErrors[token.id].invalidTokenType ? localizedValues.tokenInvalidTypeMessage : ""}
                />
              </div>
              <div className={clsx(styles.tokenDescriptionCell, styles.messageFormatInputContainer)}>
                <div className={styles.tokenDescriptionRequired}>
                  <TextInput
                    id={`token-description-${token.id}`}
                    className={clsx(descriptionsValidationErrors[token.id]?.descriptionRequired && styles.invalidField, styles.messageFormatInput)}
                    name={token.id}
                    value={token.description}
                    size={InputSize.xlarge}
                    onValueChange={value => onDescriptionChanged(index, value)}
                    onBlur={() => onDescriptionBlur(token)}
                    disabled={isReadOnlyMode}
                  />
                </div>
                <ValidationMessage
                  message={descriptionsValidationErrors[token.id] && descriptionsValidationErrors[token.id].descriptionRequired ? localizedValues.descriptionRequiredMessage : ""}
                />
              </div>
              {token.customToken && !isReadOnlyMode && (
                <div className={styles.tokenDescriptionCell}>
                  <Link id={`token-delete-${token.id}`} onClick={() => onDeleteClick(token.id)}>
                    <RedCrossMenuIcon className={styles.crossIcon} />
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!isReadOnlyMode && renderAddCustomTokenLink}
    </Form.Section>
  );
};

export default connect(
  (state: { messageFormat: IMessageIdState }) => {
    return {
      text: state.messageFormat.text,
      tokens: state.messageFormat.tokens,
      descriptionsValidationErrors: state.messageFormat.descriptionsValidationErrors
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        changeTokenType: messageFormatActions.changeTokenType,
        changeTokenDescription: messageFormatActions.changeTokenDescription,
        changeCustomTokenName: messageFormatActions.changeCustomTokenName,
        addCustomToken: messageFormatActions.addCustomToken,
        removeCustomToken: messageFormatActions.removeCustomToken,
        changeTextPreview: messageFormatActions.changeTextPreview,
        setTokensDescriptionValidationState: messageFormatActions.setTokensDescriptionValidationState
      },
      dispatch
    )
)(TokensSection);
